import { EditOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import type { DataGridProProps, GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate, useSearchParams } from 'react-router';
import { useMemo } from 'react';
import { StyleObj } from '../../../@types';
import { CRMUserData, Partner } from '../../../@types/api';
import { PARTNER_TAB_CONFIG } from '../../../config/crm';
import { QUERY_KEYS } from '../../../constants';
import { useCRMUser } from '../../../contexts/CRMUserContext';
import { useModal } from '../../../contexts/ModalContext';
import { defaultColumnsPartners } from '../../../helpers/table';
import { useInvalidateQuery } from '../../../hooks/useInvalidateQuery';
import useMutateData from '../../../hooks/useMutateData';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { createColumn } from '../../../utils';
import Switch from '../../atoms/Switch';
import WalletIcon from '../../icons/WalletIcon';
import FormModal from '../../modals/FormModal';
import CRMDetailsDrawer from '../../organisms/crm/CRMDetailsDrawer';
import TableTemplate from '../../templates/TableTemplate';
import { roleHasWallet } from '../../../constants/keycloak';
import API_ROUTES from '../../../constants/apiRoutes';
import { partnerFilterParams } from '../../organisms/forms/partnerFilterParams';

dayjs.extend(relativeTime);

const styles: StyleObj = {
  table: {
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
};

const shouldShowWalletIcon = (partner: Partner) => !!partner.userId && roleHasWallet(partner.role.name);

const PartnersPage = () => {
  const [searchParams] = useSearchParams();
  const filterQuery = useMemo(
    () => partnerFilterParams.toApiQuery(partnerFilterParams.parse(searchParams)),
    [searchParams]
  );
  const {
    data: partnersData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<Partner>(API_ROUTES.AGENT_SEARCH, { page: 1, limit: 25 }, undefined, 'crm', undefined, filterQuery);
  const { updateDataAsync } = useMutateData('agent', [QUERY_KEYS.crmUsers], 'crm');
  const { handleSort } = useSort(changeQuery, 'crm');
  const invalidateData = useInvalidateQuery();
  const navigate = useNavigate();
  const { openModal } = useModal();

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const openEditDialog = (row: Partner) => {
    openModal(<FormModal form='partners' />, row);
  };

  const updateAgentStatus = async (e: React.ChangeEvent<HTMLInputElement>, agentId: string) => {
    await updateDataAsync(
      agentId,
      { isActive: !e.target.checked },
      () => {
        invalidateData([QUERY_KEYS.agentSearch]);
        invalidateData([[QUERY_KEYS.crmUsers, agentId]]);
      },
      'Status updated successfully'
    );
  };

  const columns: GridColDef[] = [
    createColumn('fullName', 'Full Name', {
      alwaysVisible: true,
      minWidth: 150,
    }),
    createColumn('email', 'Email', {
      flex: 1.5,
      minWidth: 150,
    }),
    createColumn('createdAt', 'Date Created', {
      valueGetter: (value, row, column, apiRef) => dayjs(row.createdAt).format('DD.MM.YYYY'),
      flex: 0.75,
    }),
    createColumn('parent', 'Parent', {
      sortable: false,
      valueGetter: (value, row, column, apiRef) => row.teamLead?.fullName,
    }),
    createColumn('role', 'Role', {
      valueGetter: (value, row, column, apiRef) => row.role.name,
      flex: 0.75,
      sortable: false, // Remove once BE work is done
    }),
    createColumn('isActive', 'Status', {
      alwaysVisible: true,
      renderCell: (params: GridCellParams<Partner>) => (
        <Switch
          value={params.row.isActive}
          onChange={(e) => updateAgentStatus(e, params.row.id)}
          name='status'
          handleConfirmationMessage={() =>
            `Are you sure you want to ${params.row.isActive ? 'deactivate' : 'activate'} ${params.row.fullName}?`
          }
        />
      ),
      flex: 0.5,
    }),
    createColumn('edit', 'Actions', {
      sortable: false,
      disableExport: true,
      renderCell: (params: GridCellParams<Partner>) => {
        return (
          <>
            <IconButton onClick={() => openEditDialog(params.row)} color='primary'>
              <EditOutlined />
            </IconButton>
            {shouldShowWalletIcon(params.row) && (
              <IconButton onClick={() => navigate(`../partners/${params.row.userId}/wallet`)}>
                <WalletIcon />
              </IconButton>
            )}
          </>
        );
      },
      flex: 0.75,
    }),
  ];

  const { setSelectedCRMUser } = useCRMUser();

  const handleCellClick = (params: GridCellParams<Partner>) => {
    if (params.field !== 'edit' && params.field !== 'isActive') {
      setSelectedCRMUser(params.row as CRMUserData);
    }
  };

  return (
    <>
      <TableTemplate
        rows={partnersData?.items || []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={partnersData?.count || 0}
        createItemBtnLabel='Add new partner'
        defaultVisibleColumns={defaultColumnsPartners}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        onCellClick={handleCellClick}
        formType='partners'
        sx={styles.table as DataGridProProps['sx']}
        changeQuery={changeQuery}
        showExport
      />
      <CRMDetailsDrawer config={PARTNER_TAB_CONFIG} />
    </>
  );
};

export default PartnersPage;
